import { useParams, useNavigate, useOutletContext, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader2, ArrowLeft, FileText } from 'lucide-react';
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from 'recharts';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { getSurveyAnalytics } from '@/lib/admin/surveys-api';
import { LeaderInsightsPanel } from '@/components/admin/LeaderInsightsPanel';

export default function AdminSurveyAnalytics() {
  const { surveyId } = useParams<{ surveyId: string }>();
  useOutletContext<{ orgId: string }>();
  const navigate = useNavigate();

  const q = useQuery({
    queryKey: ['admin', 'survey-analytics', surveyId],
    queryFn: () => getSurveyAnalytics(surveyId!),
    enabled: !!surveyId,
  });

  if (q.isLoading) return <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />;
  if (q.error) return <p className="text-sm text-destructive">{(q.error as Error).message}</p>;
  if (!q.data) return null;

  const radarData = q.data.radar.map((r) => ({
    anchor: r.name,
    actual: r.suppressed || r.value == null ? 0 : Number(r.value),
    target: r.target ?? 0,
    suppressed: r.suppressed,
    n: r.n,
  }));
  const visibleCount = q.data.radar.filter((r) => !r.suppressed).length;

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/app/admin/surveys/${surveyId}`)}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to editor
        </Button>
        <Button asChild variant="outline" size="sm">
          <Link to={`/app/admin/surveys/${surveyId}/report`}>
            <FileText className="h-4 w-4 mr-2" /> Report
          </Link>
        </Button>
      </div>

      <div>
        <h2 className="text-2xl font-heading font-semibold">Analytics</h2>
        <p className="text-sm text-muted-foreground">
          k&lt;5 anchors are suppressed and excluded from the radar.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base font-heading">Culture radar</CardTitle>
          <CardDescription>Actual mean vs. target per anchor.</CardDescription>
        </CardHeader>
        <CardContent>
          {visibleCount < 3 ? (
            <p className="text-sm text-muted-foreground">
              Need at least 3 anchors with sufficient responses to draw a radar.
            </p>
          ) : (
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="anchor" tick={{ fontSize: 11 }} />
                  <PolarRadiusAxis angle={90} domain={[0, 5]} tick={{ fontSize: 10 }} />
                  <Radar name="Target" dataKey="target" stroke="hsl(var(--muted-foreground))" fill="hsl(var(--muted))" fillOpacity={0.3} />
                  <Radar name="Actual" dataKey="actual" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.5} />
                  <Legend />
                  <Tooltip />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base font-heading">Tensions</CardTitle>
          <CardDescription>Anchors where current mean falls below target.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {q.data.tensions.length === 0 ? (
            <p className="text-sm text-muted-foreground">No visible gaps below target right now.</p>
          ) : (
            q.data.tensions.map((t) => (
              <div
                key={t.anchor_id}
                className="border border-border rounded-md p-3 flex items-center justify-between bg-card"
              >
                <div>
                  <div className="font-medium">{t.name}</div>
                  <div className="text-xs text-muted-foreground">n = {t.n}</div>
                </div>
                <Badge variant="destructive">gap {t.gap.toFixed(2)}</Badge>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <LeaderInsightsPanel surveyId={surveyId!} />
    </div>
  );
}
